import React from "react";
import { Modal, Button, Table } from "react-bootstrap";
import "./InvoicePrint.css";

const PrintInvoiceModal = ({ show, onClose, invoiceData, userDetails }) => {
    const patientGender = { 'M': 'Male', 'F': 'Female', 'O': 'Other' };
    const patientSalutation = { '1': 'DR.', '2': 'MR.', '3': 'MRS.', '4': 'MISS', '5': 'MASTER' }
    const patientPaymentMode = { 'cash': 'Cash', 'card': 'Card', 'upi': 'UPI', 'other': 'Other' };
    const patientPaymentTypes = { 'full': 'Full Payment', 'installment': 'Installment' };
    const patientTreatmentStatus = { 'planned': 'Planned', 'in-progress': 'In Progress', 'completed': 'Completed' };

    const {
        user_type,
        clinic_name,
        doctor_name,
        doctor_profession,
        registration_number,
        doctor_degree,
        mobile_number: doctor_mobile_number,
        alternate_mobile_number: doctor_alternate_mobile_number,
        email_id: doctor_email_id,
        clinic_address,
        clinic_website
    } = userDetails;

    const {
        invoice_number,
        source_type,
        created_on,
        clinic: clinic_id,
        patient = {},
        source_data = {},
    } = invoiceData;

    const {
        patient_id,
        salutation,
        patient_name,
        patient_case_number,
        gender,
        age,
        mobile_number,
    } = patient || {};

    const {
        amount: total_amount_raw,
        amount_paid_via,
        date: source_date,
        follow_up,
        diagnosis_1,
        diagnosis_2,
        diagnosis_3,
        diagnosis_4,
        advice,
        next_appointment_date,
        next_appointment_time,
        treatment_items = [],
        bulk_group = null,
        paid_amount: total_paid_raw,
        balance: total_balance_raw,
        payment_history = [],
    } = source_data || {};

    const diagnosisList = [diagnosis_1, diagnosis_2, diagnosis_3, diagnosis_4].filter(Boolean);

    const onHandlePrint = () => {
        const printContent = document.getElementById("a4-content").innerHTML;
        const printWindow = window.open("", "_blank", "height=600,width=800");

        // Extract styles from the document
        let styles = "";
        for (let sheet of document.styleSheets) {
            try {
                for (let rule of sheet.cssRules) {
                    styles += rule.cssText;
                }
            } catch (error) {
                console.warn("Cannot access stylesheet:", error);
            }
        }

        printWindow.document.write(`
            <html>
            <head>
                <title>Invoice</title>
                <style>${styles}</style>
            </head>
            <body>
                <div id="invoice-print" class="invoice-container">
                    ${printContent}
                </div>
            </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";

        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString("en-US", { month: "short" });
        const year = date.getFullYear();

        // Function to add "st", "nd", "rd", or "th" to the day
        const getDaySuffix = (day) => {
            if (day >= 11 && day <= 13) return "th"; // Special case for 11th, 12th, 13th
            switch (day % 10) {
                case 1:
                    return "st";
                case 2:
                    return "nd";
                case 3:
                    return "rd";
                default:
                    return "th";
            }
        };

        return `${day}${getDaySuffix(day)} ${month} ${year}`;
    };

    const formatTime = (timeString) => {
        if (!timeString) return "";

        const [hours, minutes] = timeString.split(":").map(Number);
        const date = new Date();
        date.setHours(hours, minutes);

        return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
        });
    };

    const safeNumber = (v) => {
        if (v === null || v === undefined || v === "") return 0;
        // keep numbers as numbers if possible
        const n = Number(v);
        return isNaN(n) ? 0 : n;
    };

    // Handle amounts differently for consultation vs dental invoices
    let total_original = 0; // sum of original_cost (or cost)
    let total_discount = 0; // sum of discount_amount
    let total_final = 0;    // sum of final_cost (after discount) - fallback to source amount
    let total_paid = 0;
    let total_balance = 0;
    let total_paid_till = 0;

    if (source_type === "personal" || source_type === "followup") {
        total_original = safeNumber(total_amount_raw); // treat as final for consultation
        total_discount = 0;
        total_final = total_original;
        total_paid = total_final; // assume paid at consultation
        total_balance = 0;
    } else if (source_type === "dental" || source_type === "bulk_dental") {
        const isInstallment = treatment_items.length > 0 && treatment_items[0].payment_type === "installment";
        total_original = treatment_items.reduce((s, t) => s + safeNumber(t.original_cost || t.cost || t.final_cost), 0);
        total_discount = treatment_items.reduce((s, t) => s + safeNumber(t.discount_amount || 0), 0);

        const sum_final_from_items = treatment_items.reduce((s, t) => s + safeNumber(t.final_cost !== undefined ? t.final_cost : 0), 0);
        total_final = sum_final_from_items > 0 ? sum_final_from_items : (total_original - total_discount);

        if (isInstallment) {
            const allPayments = payment_history.map(p => safeNumber(p.amount));
            const currentPayment = safeNumber(total_paid_raw || source_data?.amount || 0);
            const previousPayments = allPayments.length > 0 ? allPayments.slice(0, -1).reduce((a, b) => a + b, 0) : 0;

            total_paid_till = previousPayments;
            total_paid = currentPayment;
            total_balance = safeNumber(total_final - (total_paid_till + total_paid));
        } else {
            total_paid = safeNumber(total_paid_raw !== undefined ? total_paid_raw : treatment_items.reduce((s, t) => s + safeNumber(t.paid_amount), 0));
            total_balance = safeNumber(total_balance_raw !== undefined ? total_balance_raw : (total_final - total_paid));
        }
    }

    // Render functions for blocks
    const renderConsultationTable = () => (
        <Table bordered responsive className="prescription-table">
            <thead>
                <tr>
                    <th>Description</th>
                    <th>Amount (₹)</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>{source_type === "followup" ? "Follow-up Consultation Fee" : "First Consultation Fee"}</td>
                    <td>₹{total_paid.toFixed(2)}</td>
                </tr>
            </tbody>
        </Table>
    );

    const renderDentistTreatmentTable = () => {
        if (!treatment_items || treatment_items.length === 0) return null;

        // If bulk group exists, group treatments by type (to collapse teeth list)
        let displayItems = [];

        if (bulk_group) {
            // Group treatments by treatment_type
            const grouped = treatment_items.reduce((acc, t) => {
                const key = t.treatment_type;
                if (!acc[key]) acc[key] = [];
                acc[key].push(t);
                return acc;
            }, {});

            // Flatten to summarized entries
            displayItems = Object.entries(grouped).map(([treatment_type, items]) => {
                const toothList = items.map((i) => i.tooth_number).sort((a, b) => a - b);
                return {
                    treatment_type,
                    tooth_numbers: toothList.join(", "),
                    treatment_status: items[0].treatment_status,
                    treatment_date: items[0].treatment_date,
                    payment_type: items[0].payment_type,
                };
            });
        } else {
            // Normal non-bulk case → show all individually
            displayItems = treatment_items.map((t) => ({
                treatment_type: t.treatment_type,
                tooth_numbers: t.tooth_number,
                treatment_status: t.treatment_status,
                treatment_date: t.treatment_date,
                payment_type: t.payment_type,
            }));
        }

        return (
            <>
                <>
                    <Table bordered responsive className="prescription-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Tooth No.</th>
                                <th>Treatment</th>
                                <th>Treatment Status</th>
                                <th>Treatment Date</th>
                                <th>Payment Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayItems.map((t, idx) => {
                                return (
                                    <tr key={t.treatment_id || idx}>
                                        <td>{idx + 1}</td>
                                        <td>{t.tooth_numbers}</td>
                                        <td>{t.treatment_type}</td>
                                        <td>{patientTreatmentStatus[t.treatment_status]}</td>
                                        <td>{formatDate(t.treatment_date)}</td>
                                        <td>{patientPaymentTypes[t.payment_type]}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>

                    {/* Bulk group info (if present) */}
                    {bulk_group && (
                        <div className="mb-3">
                            <strong>Bulk Group:</strong> {bulk_group.name || bulk_group.dental_bulk_id || ""}
                        </div>
                    )}
                </>
            </>
        );
    };

    return (
        <Modal show={show} onHide={onClose} size="xl" backdrop="static" keyboard={false} centered>
            <Modal.Header closeButton>
                <Modal.Title>Invoice Details</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4 overflow-auto a4-modal-body">
                <div id="a4-content" className="a4-page">
                    <div className="header-bottom pb-3 mb-4" style={{ backgroundColor: "#ffffff" }}>
                        {/* Clinic Header */}
                        <div className="invoice-header d-flex align-items-center justify-content-between">
                            {/* Left: Clinic Logo */}
                            <div className="clinic-logo-wrapper text-start">
                                <img
                                    src={`${window.location.origin}/clinic-logo.png`}
                                    alt="Clinic Logo"
                                    className="clinic-logo"
                                />
                            </div>

                            {/* Right: Clinic Details */}
                            <div className="clinic-details text-end">
                                <div id="clinic_name" className="prescription-clinic-name" style={{
                                    fontSize: clinic_name?.length > 60 ? "1.2rem" : clinic_name?.length > 40 ? "1.4rem" : "1.8rem",
                                }}>
                                    {(() => {
                                        if (!clinic_name) return null;
                                        const words = clinic_name.trim().split(/\s+/);
                                        const fullName = clinic_name.trim();

                                        if (fullName.length <= 30) {
                                            return <div>{fullName}</div>;  // Short name = 1 line
                                        }

                                        // Long name = split to 2 lines at middle word
                                        const halfway = Math.ceil(words.length / 2);
                                        const firstLine = words.slice(0, halfway).join(" ");
                                        const secondLine = words.slice(halfway).join(" ");
                                        return (
                                            <>
                                                <div>{firstLine}</div>
                                                <div>{secondLine}</div>
                                            </>
                                        );
                                    })()}
                                </div>
                                <h5><strong>{doctor_name}</strong></h5>
                                <div>{doctor_profession}</div>
                                <div>{doctor_degree}, Reg. No: {registration_number}</div>
                                <div>Email: {doctor_email_id}</div>
                                <div>
                                    Mobile Number: {doctor_mobile_number}
                                    {doctor_alternate_mobile_number && (<> / {doctor_alternate_mobile_number}</>)}
                                </div>
                                {clinic_website && (
                                    <div>
                                        Website: <a href={clinic_website} target="_blank" rel="noopener noreferrer">{clinic_website}</a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Divider Line and Tax Invoice in Center */}
                    <div className="text-center mb-4">
                        <h4 className="mt-2"><strong>Tax Invoice</strong></h4>
                        <hr />
                    </div>

                    {/* Invoice Details & Visit Date */}
                    <div className="d-flex justify-content-between align-items-start mb-4">
                        <div>
                            <div><strong>Invoice Number:</strong> {invoice_number}</div>
                            {patient_case_number && (
                                <div><strong>Patient Case No:</strong> {patient_case_number}</div>
                            )}
                            <br />
                            <div><strong>Patient Name:</strong> {patientSalutation[salutation]} {patient_name}</div>
                            <div><strong>Gender / Age:</strong> {patientGender[gender]} / {age} yrs</div>
                            <div><strong>Mobile Number:</strong> +91-{mobile_number}</div>
                        </div>
                        <div>
                            <div><strong>Date:</strong> {formatDate(source_date)}</div>
                        </div>
                    </div>

                    {/* Main invoice body: dentist vs general */}
                    {source_type === "dental" || source_type === "bulk_dental" ? renderDentistTreatmentTable() : null}
                    {source_type === "personal" || source_type === "followup" ? renderConsultationTable() : null}

                    {/* Total & Payment Summary */}
                    <div className="invoice-summary-box mt-4 mb-4">
                        <h6 className="summary-title mb-2">Payment Summary</h6>
                        <div className="d-flex justify-content-end">
                            <table className="summary-table">
                                <tbody>
                                    {/* Show Original Total only for dental invoices (if >0) */}
                                    {(source_type === "dental" || source_type === "bulk_dental") && (
                                        <tr>
                                            <td>Original Total</td>
                                            <td>₹{total_original.toFixed(2)}</td>
                                        </tr>
                                    )}
                                    {/* Discount row (show only if > 0) */}
                                    {(source_type === "dental" || source_type === "bulk_dental") && total_discount > 0 && (
                                        <tr>
                                            <td>Discount</td>
                                            <td>- ₹{total_discount.toFixed(2)}</td>
                                        </tr>
                                    )}
                                    {/* Net / Final total */}
                                    <tr>
                                        <td><strong>Net Total</strong></td>
                                        <td><strong>₹{total_final.toFixed(2)}</strong></td>
                                    </tr>

                                    {/* Show Amount Paid Till (for Installments only) */}
                                    {(source_type === "dental" || source_type === "bulk_dental") &&
                                        treatment_items.length > 0 && treatment_items[0].payment_type === "installment" && (
                                            <tr>
                                                <td>Amount Paid Till</td>
                                                <td>₹{total_paid_till.toFixed(2)}</td>
                                            </tr>
                                        )}
                                    {/* Show paid & balance only for dental flows */}
                                    <tr>
                                        <td>Amount Paid</td>
                                        <td>₹{total_paid.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td>Balance Due</td>
                                        <td>₹{total_balance.toFixed(2)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="text-end mt-2">
                            <span className="text-muted"><strong>Payment Mode:</strong> {patientPaymentMode[amount_paid_via]}</span>
                        </div>

                        {(source_type === "dental" || source_type === "bulk_dental") && total_balance === 0 && (
                            <div className="paid-stamp mt-3 text-end">
                                <span>PAID IN FULL</span>
                            </div>
                        )}
                    </div>

                    {/* Installment Payment History */}
                    {(source_type === "dental" || source_type === "bulk_dental") && payment_history.length > 1 && (
                        <div className={`payment-history-section mt-4 ${payment_history.length >= 6 ? "page-break-before" : ""}`}>
                            <h6 className="summary-title mb-2">Payment History</h6>
                            <Table bordered responsive className="prescription-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Invoice No.</th>
                                        <th>Payment Date</th>
                                        <th>Amount (₹)</th>
                                        <th>Payment Mode</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payment_history.map((p, idx) => (
                                        <tr key={idx}>
                                            <td>{idx + 1}</td>
                                            <td>{p.invoice_number}</td>
                                            <td>{formatDate(p.date)}</td>
                                            <td>₹{safeNumber(p.amount).toFixed(2)}</td>
                                            <td>{patientPaymentMode[p.amount_paid_via]}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Doctor’s Notes Section */}
                    {(source_type === "personal" || source_type === "followup") && (
                        <>
                            {(diagnosisList.length > 0 || follow_up || advice || next_appointment_date || next_appointment_time) && (
                                <div className="doctors-notes-section mt-4">
                                    <h5 className="notes-title mb-3">Doctor’s Notes</h5>

                                    {diagnosisList.length > 0 && (
                                        <div className="mb-3">
                                            <strong>Diagnosis:</strong>
                                            <ul className="notes-list">
                                                {diagnosisList.map((d, i) => (
                                                    <li key={i}>{d}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {follow_up && (
                                        <div className="mb-3">
                                            <strong>Follow Up:</strong>
                                            <div className="notes-text">{follow_up}</div>
                                        </div>
                                    )}

                                    {advice && (
                                        <div className="mb-3">
                                            <strong>Advice:</strong>
                                            <div className="notes-text">{advice}</div>
                                        </div>
                                    )}

                                    {(next_appointment_date || next_appointment_time) && (
                                        <div className="mb-2">
                                            <strong>Next Appointment:</strong>{" "}
                                            {next_appointment_date ? formatDate(next_appointment_date) : ""}
                                            {next_appointment_time ? ` ${formatTime(next_appointment_time)}` : ""}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {/* Footer Section */}
                    <footer className="clinic-footer">
                        {/* Clinic Address */}
                        <div className="clinic-address">
                            {clinic_address.split("\n").map((line, index) => (
                                <p key={index} style={{ margin: 0 }}>{line}</p>
                            ))}
                        </div>

                        {/* Disclaimer included here */}
                        <div className="disclaimer-section mt-2">
                            <p className="software-credit">Generated by VisionThink Tech - Clinic Management Software</p>
                        </div>
                    </footer>

                    {/* Signature Section */}
                    <div className="signature-container mb-3">
                        <>
                            <p className="doctor-name-placeholder">{doctor_name}</p>
                            <div className="signature-box no-line no-padding">
                                <span className="signature-text">Signature</span>
                            </div>
                        </>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="primary" onClick={onHandlePrint} disabled={user_type === "M"}>Print</Button>
                <Button variant="secondary" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PrintInvoiceModal;
